import { Modal, ModalProvider, useNotifications } from '@joor/design-system'
import styled from 'styled-components'

import usePortal from 'hooks/usePortal'

import { useSaveBasicInfo } from '../RetailerProfileEditMode/RetailerProfileEditMode.hooks'
import { getTaxVerificationModalUpdateInput } from '../RetailerProfileEditMode/RetailerProfileEditMode.utils'
import { ViewModeTaxVerificationIds } from '../retailerProfilePage.ids'
import type { RetailerProfile } from '../retailerProfilePage.types'
import { TaxIdVerificationFields } from './TaxIdVerificationFields'
import { taxIdProValidCountries } from './taxIdProValidCountries'
import { useTaxIdFields } from './useTaxIdFields'

const StyledModal = styled(Modal)`
  > div:first-of-type {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  #${ViewModeTaxVerificationIds.ModalTitle} svg path,
  #${ViewModeTaxVerificationIds.ModalTitle} svg circle {
    fill: var(--color-secondary-800);
  }
`

type TaxIdVerificationModalProps = {
  isOpen: boolean
  onClose: () => void
  onTaxSaveSuccess?: (hasTaxIdOnProfile: boolean) => void
  retailerProfile: RetailerProfile
  onProfileUpdated?: () => void
}

export function TaxIdVerificationModal({
  isOpen,
  onClose,
  onTaxSaveSuccess,
  retailerProfile,
  onProfileUpdated,
}: TaxIdVerificationModalProps) {
  const Portal = usePortal()
  const { addToasts } = useNotifications()
  const { saveBasicInfo, savingBasicInfo } = useSaveBasicInfo()
  const tax = useTaxIdFields(retailerProfile)

  const handleClose = () => {
    if (!savingBasicInfo) {
      onClose()
    }
  }

  const handleAddToProfile = async () => {
    const outcome = await tax.verifyAndNormalizeForSave()
    if (!outcome.ok) {
      return
    }

    const saved = await saveBasicInfo(
      getTaxVerificationModalUpdateInput({
        retailerProfile,
        taxId: outcome.taxId,
        taxIdCountry: outcome.taxIdCountry,
      }),
    )

    if (!saved) {
      addToasts([
        {
          type: 'error',
          title: 'Saving Error',
          description: 'There was an error updating your profile.',
        },
      ])
      return
    }

    const clearedTax = !outcome.taxId && !outcome.taxIdCountry
    addToasts([
      {
        type: 'success',
        title: clearedTax ? 'Profile updated' : 'Profile Verified',
        description: clearedTax
          ? 'Your tax verification details were removed from your profile.'
          : 'Brands will now see you as verified.',
      },
    ])
    onTaxSaveSuccess?.(Boolean(outcome.taxId))
    onProfileUpdated?.()
    onClose()
  }

  return (
    <Portal>
      <ModalProvider>
        <StyledModal
          id={ViewModeTaxVerificationIds.Modal}
          isOpen={isOpen}
          title={{
            text: 'Verification',
            iconName: 'check-circle-on',
            id: ViewModeTaxVerificationIds.ModalTitle,
          }}
          size="small"
          onClose={handleClose}
        >
          <Modal.Content>
            <TaxIdVerificationFields
              countryOptions={taxIdProValidCountries}
              countryOption={tax.countryOption}
              taxId={tax.taxId}
              errors={tax.errors}
              taxIdVerified={tax.taxIdVerified}
              onCountrySelect={tax.handleCountrySelect}
              onTaxIdChange={tax.handleTaxIdChange}
              onTaxIdBlur={tax.verifyOnBlur}
            />
          </Modal.Content>
          <Modal.Actions
            primaryButton={{
              text: 'Add to profile',
              handler: handleAddToProfile,
              loading: savingBasicInfo,
            }}
          />
        </StyledModal>
      </ModalProvider>
    </Portal>
  )
}
